import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  SignOut, Buildings, ChartLine, ShieldCheck, EnvelopeSimple, ClockCounterClockwise, List as ListIcon, Compass, Bank, CalendarBlank, Wrench, ChatsCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavConfig } from "@/lib/navConfig";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import Brand from "@/components/Brand";
import { Bell, CaretDown, ShieldStar } from "@phosphor-icons/react";

const ROLE_LABELS = {
  admin: "Admin",
  secondary_admin: "Secondary admin",
  view_only: "View only",
};

export default function AppHeader({ sticky = true } = {}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [notesUnread, setNotesUnread] = useState(0);
  const navCfg = useNavConfig();

  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";
  const canTimesheet = !!user?.permissions?.timesheet_access;
  const canAtlas = !!user?.is_super_admin;
  const canLoans = !!user?.is_super_admin || !!user?.permissions?.loans_access;
  const canInsurance = !!user?.is_super_admin || user?.permissions?.insurance_access !== false;
  const canSubcontractor = !!user?.is_super_admin || user?.permissions?.subcontractor_access !== false;
  const canEin = !!user?.is_super_admin || user?.permissions?.ein_access !== false;
  const canLicenses = !!user?.is_super_admin || user?.permissions?.licenses_access !== false;

  // Reminders sub-navigation: expandable list so new modules (subcontractor
  // insurance, licenses/permits) can be added by pushing another entry here.
  const remindersItems = [
    canInsurance ? { to: "/insurance", label: "Insurance", testId: "nav-insurance" } : null,
    canSubcontractor ? { to: "/subcontractors", label: "Subcontractor Insurance", testId: "nav-subcontractors" } : null,
    canEin ? { to: "/ein", label: "EIN", testId: "nav-ein" } : null,
    canLicenses ? { to: "/licenses", label: "Licenses / Permits / Reminders", testId: "nav-licenses" } : null,
  ].filter(Boolean);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [n, m] = await Promise.all([
          api.get("/admin/notifications").catch(() => ({ data: [] })),
          api.get("/notes/unread-count").catch(() => ({ data: { count: 0 } })),
        ]);
        if (cancelled) return;
        setUnread((n.data || []).filter((x) => !x.is_read).length);
        setNotesUnread(m.data?.count || 0);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdmin, location.pathname]);

  const topItems = {
    "dashboard": (
      <>
        <NavItem to="/dashboard" active={location.pathname === "/dashboard"} icon={<ChartLine size={16} weight="bold" />} testId="nav-dashboard">
          Dashboard
        </NavItem>
      </>
    ),
    "businesses": (
      <>
        <NavItem to="/businesses" active={location.pathname === "/businesses"} icon={<Buildings size={16} weight="bold" />} testId="nav-businesses">
          Businesses
        </NavItem>
      </>
    ),
    "timesheet": (
      <>
        {canTimesheet && (
          <NavItem to="/timesheet" active={location.pathname === "/timesheet"} icon={<ClockCounterClockwise size={16} weight="bold" />} testId="nav-timesheet">
            Timesheet
          </NavItem>
        )}
      </>
    ),
    "atlas": (
      <>
        {canAtlas && (
          <NavItem to="/atlas" active={location.pathname === "/atlas"} icon={<Compass size={16} weight="bold" />} testId="nav-atlas">
            Atlas
          </NavItem>
        )}
      </>
    ),
    "loans": (
      <>
        {canLoans && (
          <NavItem to="/loans" active={location.pathname === "/loans"} icon={<Bank size={16} weight="bold" />} testId="nav-loans">
            Assets
          </NavItem>
        )}
      </>
    ),
    "reminders": (
      <>
        {remindersItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-white/10 text-white/90`}
                data-testid="nav-reminders-toggle"
              >
                <Bell size={16} weight="bold" /> Reminders <CaretDown size={11} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[200px]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Reminders</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {remindersItems.map((item) => (
                <DropdownMenuItem key={item.to} asChild data-testid={`${item.testId}-menu-item`}>
                  <Link to={item.to} className="cursor-pointer w-full">{item.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </>
    ),
    "schedule": (
      <>
        <NavItem to="/schedule" active={location.pathname === "/schedule"} icon={<CalendarBlank size={16} weight="bold" />} testId="nav-schedule">
          Schedule
        </NavItem>
      </>
    ),
    "service": (
      <>
        <NavItem to="/service" active={location.pathname === "/service"} icon={<Wrench size={16} weight="bold" />} testId="nav-service">
          Service
        </NavItem>
      </>
    ),
    "daily-logs": (
      <>
        <NavItem to="/daily-logs" active={location.pathname === "/daily-logs"} icon={<ChatsCircle size={16} weight="bold" />} testId="nav-daily-logs">
          Daily Logs
        </NavItem>
      </>
    ),
  };

  const initials = (user?.name || user?.email || "U").trim().slice(0, 2).toUpperCase();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className={`bg-[#1f64c2] text-white ${sticky ? "sticky top-0" : ""} z-50 h-16 flex items-center px-4 sm:px-6 lg:px-8 shadow-sm`}>
      {/* Hamburger drawer — always visible, mobile + desktop */}
      <Sheet>
        <SheetTrigger asChild>
          <button
            className="mr-3 -ml-1 rounded-md p-2 hover:bg-white/10 transition-colors text-white"
            aria-label="Open navigation"
            data-testid="nav-hamburger-button"
          >
            <ListIcon size={22} weight="bold" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 p-0">
          <div className="bg-[#1f64c2] text-white px-5 py-4">
            <Brand size="md" color="navy" />
          </div>
          <nav className="p-3 flex flex-col gap-1" data-testid="nav-drawer">
            <DrawerNavItem to="/dashboard" active={location.pathname === "/dashboard"} icon={<ChartLine size={18} weight="bold" />} testId="drawer-nav-dashboard">
              Dashboard
            </DrawerNavItem>
            <DrawerNavItem to="/businesses" active={location.pathname === "/businesses"} icon={<Buildings size={18} weight="bold" />} testId="drawer-nav-businesses">
              Businesses
            </DrawerNavItem>
            {canTimesheet && (
              <DrawerNavItem to="/timesheet" active={location.pathname === "/timesheet"} icon={<ClockCounterClockwise size={18} weight="bold" />} testId="drawer-nav-timesheet">
                Timesheet
              </DrawerNavItem>
            )}
            {canAtlas && (
              <DrawerNavItem to="/atlas" active={location.pathname === "/atlas"} icon={<Compass size={18} weight="bold" />} testId="drawer-nav-atlas">
                Atlas
              </DrawerNavItem>
            )}
            {canLoans && (
              <DrawerNavItem to="/loans" active={location.pathname === "/loans"} icon={<Bank size={18} weight="bold" />} testId="drawer-nav-loans">
                Assets
              </DrawerNavItem>
            )}
            {remindersItems.length > 0 && (
              <DrawerRemindersGroup items={remindersItems} activePath={location.pathname} />
            )}
            <DrawerNavItem to="/schedule" active={location.pathname === "/schedule"} icon={<CalendarBlank size={18} weight="bold" />} testId="drawer-nav-schedule">
              Schedule
            </DrawerNavItem>
            <DrawerNavItem to="/service" active={location.pathname === "/service"} icon={<Wrench size={18} weight="bold" />} testId="drawer-nav-service">
              Service
            </DrawerNavItem>
            <DrawerNavItem to="/daily-logs" active={location.pathname === "/daily-logs"} icon={<ChatsCircle size={18} weight="bold" />} testId="drawer-nav-daily-logs">
              Daily Logs
            </DrawerNavItem>
            {isAdmin && (
              <DrawerNavItem to="/admin" active={location.pathname === "/admin"} icon={<ShieldCheck size={18} weight="bold" />} testId="drawer-nav-admin">
                Admin
                <span className="ml-auto inline-flex items-center gap-1">
                  {notesUnread > 0 && (
                    <span className="text-[10px] bg-emerald-500 text-white rounded-full px-1.5 py-[1px] min-w-[18px] inline-flex items-center justify-center font-semibold" data-testid="header-notes-pill-drawer">
                      {notesUnread}
                    </span>
                  )}
                  {user?.is_super_admin && unread > 0 && (
                    <span className="text-[10px] bg-[#D92D20] text-white rounded-full px-1.5 py-[1px] min-w-[18px] inline-flex items-center justify-center font-semibold">
                      {unread}
                    </span>
                  )}
                </span>
              </DrawerNavItem>
            )}
            <div className="my-2 border-t border-slate-200" />
            <button
              onClick={onLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#D92D20] hover:bg-rose-50 transition-colors w-full text-left"
              data-testid="drawer-nav-signout"
            >
              <SignOut size={18} weight="bold" />
              Sign out
            </button>
          </nav>
        </SheetContent>
      </Sheet>

      <Brand size="md" color="navy" />

      <nav className="ml-10 hidden md:flex items-center gap-1" data-testid="top-nav">
        {navCfg.order.filter((k) => !navCfg.hidden.includes(k)).map((k) => <span key={k} className="contents">{topItems[k] || null}</span>)}
      </nav>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {isAdmin && (
          <NavItem
            to="/admin"
            active={location.pathname === "/admin"}
            icon={<ShieldCheck size={16} weight="bold" />}
            testId="nav-admin"
          >
            <span className="hidden sm:inline">Admin</span>
            {notesUnread > 0 && (
              <span className="ml-1 text-[10px] bg-emerald-500 rounded-full px-1.5 py-[1px] min-w-[18px] inline-flex items-center justify-center font-semibold" data-testid="header-notes-pill">
                {notesUnread}
              </span>
            )}
            {user?.is_super_admin && unread > 0 && (
              <span className="ml-1 text-[10px] bg-[#D92D20] rounded-full px-1.5 py-[1px] min-w-[18px] inline-flex items-center justify-center font-semibold">
                {unread}
              </span>
            )}
          </NavItem>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-white/10 transition-colors" data-testid="user-menu-button">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-[#327de3] text-white text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-sm text-white/90 max-w-[140px] truncate">{user?.name || user?.email}</span>
                <span className="text-[10px] text-white/60 uppercase tracking-widest">{ROLE_LABELS[user?.role] || "—"}</span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
              <div className="font-semibold text-slate-900">{user?.name}</div>
              <div className="truncate">{user?.email}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-[#327de3] font-semibold">
                {ROLE_LABELS[user?.role] || "—"}{user?.is_super_admin ? " · Super" : ""}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-[#D92D20] focus:text-[#D92D20]" data-testid="logout-button">
              <SignOut size={16} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function NavItem({ to, active, children, icon, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${
        active ? "bg-white/15 text-white" : "text-white/70 hover:text-white hover:bg-white/10"
      }`}
    >
      {icon}
      {children}
    </Link>
  );
}

function DrawerNavItem({ to, active, children, icon, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
        active
          ? "bg-[#327de3]/10 text-[#1f64c2]"
          : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      {icon}
      <span className="flex-1 flex items-center">{children}</span>
    </Link>
  );
}

// Collapsible Reminders group inside the hamburger drawer. Auto-expands
// when any child route is active; toggle state persists in state only.
function DrawerRemindersGroup({ items, activePath }) {
  const hasActive = items.some((it) => activePath === it.to);
  const [open, setOpen] = useState(hasActive);
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);
  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium w-full text-left transition-colors ${hasActive ? "bg-[#327de3]/10 text-[#1f64c2]" : "text-slate-700 hover:bg-slate-100"}`}
        data-testid="drawer-nav-reminders-toggle"
      >
        <Bell size={18} weight="bold" />
        <span className="flex-1">Reminders</span>
        <CaretDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-6 mt-1 space-y-0.5" data-testid="drawer-nav-reminders-list">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`drawer-${it.testId}`}
              className={`block px-3 py-2 rounded-md text-sm ${activePath === it.to ? "bg-[#327de3]/10 text-[#1f64c2] font-semibold" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

